import React, { useEffect, useRef, useState } from "react";
import axiosInstance, { weburl } from "../../../axiosInstance";
import Sidebar from "../../components/Sidebar";
import Navbar from "../../components/Navbar";
import LoadingScreen from "../../components/LoadingScreen";

const getProfileImage = (user) => {
  const image = user?.profile || user?.ProfileFile || user?.profileImage || user?.avatar || "";
  if (!image || typeof image !== "string") return "";
  if (image.startsWith("http") || image.startsWith("data:") || image.startsWith("blob:")) return image;
  const cleanImage = image.replace(/\\/g, "/").replace(/^public\//, "");
  const baseUrl = axiosInstance.defaults.baseURL || weburl;
  return `${baseUrl.replace(/\/$/, "")}/${cleanImage.replace(/^\//, "")}`;
};

const mergeProfileFields = (user, profileUser) => {
  if (!profileUser) return user;
  return {
    ...user,
    profile: user?.profile || profileUser.profile || profileUser.ProfileFile || "",
    ProfileFile: user?.ProfileFile || profileUser.ProfileFile || profileUser.profile || "",
    profileImage: user?.profileImage || profileUser.profileImage || "",
    avatar: user?.avatar || profileUser.avatar || "",
  };
};

const UserAvatar = ({ user, size = 40 }) => {
  const [imageError, setImageError] = useState(false);
  const image = getProfileImage(user);

  if (image && !imageError) {
    return (
      <img
        src={image}
        alt={user?.username || "Driver"}
        className="rounded-circle"
        style={{ width: size, height: size, objectFit: "cover", minWidth: size }}
        onError={() => setImageError(true)}
      />
    );
  }

  return (
    <div
      className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold"
      style={{ width: size, height: size, minWidth: size }}
    >
      {(user?.username || "D")[0].toUpperCase()}
    </div>
  );
};

const LiveUser = () => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef({});
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all, online, offline

  const onlineIcon = "http://maps.google.com/mapfiles/ms/icons/green-dot.png";
  const offlineIcon = "http://maps.google.com/mapfiles/ms/icons/red-dot.png";

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const [res, profileRes] = await Promise.all([
          axiosInstance.get(
            "/all-employee?type=1&longitude=&latitude=&empType=2&status=1"
          ),
          axiosInstance.get("/admin/all-user?page=1&limit=1000&type=1&empType=2&status=1"),
        ]);
        const profilesById = new Map((profileRes.data.users || []).map((user) => [user._id, user]));
        const userList = (res.data.users || [])
          .filter((user) => String(user.status) !== "0")
          .map((user) => mergeProfileFields(user, profilesById.get(user._id)));
        setUsers(userList);
        setFilteredUsers(userList);
        setLoading(false);

        if (window.google) {
          initGoogleMap(userList);
        } else {
          console.error("Google Maps API not loaded");
        }
      } catch (error) {
        console.error("Failed to load users", error);
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [searchTerm, filterStatus, users]);

  const filterUsers = () => {
    let filtered = users;

    // Filter by search term
    if (searchTerm.trim() !== "") {
      filtered = filtered.filter((user) =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (filterStatus === "online") {
      filtered = filtered.filter((user) => user.online === 1);
    } else if (filterStatus === "offline") {
      filtered = filtered.filter((user) => user.online !== 1);
    }

    setFilteredUsers(filtered);
  };

  const initGoogleMap = (userList) => {
    if (!mapRef.current) return;

    const firstUser = userList[0];
    const center = firstUser
      ? {
          lat: parseFloat(firstUser.latitude),
          lng: parseFloat(firstUser.longitude),
        }
      : { lat: 28.6139, lng: 77.209 };

    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 13,
    });

    mapInstance.current = map;

    userList.forEach((user) => {
      const lat = parseFloat(user.latitude);
      const lng = parseFloat(user.longitude);
      if (isNaN(lat) || isNaN(lng)) return;

      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        map,
        title: user.username,
        icon: user.online === 1 ? onlineIcon : offlineIcon,
      });

      const infoWindow = new window.google.maps.InfoWindow({
        content: `<strong>${user.username}</strong><br>Status: ${
          user.online ? "🟢 Online" : "🔴 Offline"
        }`,
      });

      marker.addListener("click", () => {
        infoWindow.open(map, marker);
      });

      markersRef.current[user._id] = {
        marker,
        infoWindow,
      };
    });
  };

  const handleUserClick = (userId) => {
    const userMarker = markersRef.current[userId];
    if (userMarker && mapInstance.current) {
      const marker = userMarker.marker;
      const infoWindow = userMarker.infoWindow;

      mapInstance.current.panTo(marker.getPosition());
      mapInstance.current.setZoom(17);
      infoWindow.open(mapInstance.current, marker);
    }
  };

  const showAllUsersOnMap = () => {
    const bounds = new window.google.maps.LatLngBounds();
    Object.values(markersRef.current).forEach(({ marker }) => {
      bounds.extend(marker.getPosition());
    });

    if (mapInstance.current && !bounds.isEmpty()) {
      mapInstance.current.fitBounds(bounds);
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    setFilterStatus("all");
  };

  const onlineCount = users.filter((u) => u.online === 1).length;
  const offlineCount = users.length - onlineCount;

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="py-4 px-4">
          {/* Header Section */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-0">
              <i className="bi bi-geo-alt-fill text-primary me-2"></i>
              Live Driver Tracking
            </h2>
            <div className="d-flex gap-2">
              <div className="badge bg-success fs-6 px-3 py-2">
                <i className="bi bi-circle-fill me-1"></i>
                {onlineCount} Online
              </div>
              <div className="badge bg-secondary fs-6 px-3 py-2">
                <i className="bi bi-circle-fill me-1"></i>
                {offlineCount} Offline
              </div>
            </div>
          </div>

          {loading ? (
            <LoadingScreen />
          ) : (
            <div className="row g-3">
              {/* Left Column - Search & Driver List */}
              <div className="col-md-4">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body p-0">
                    {/* Search Section */}
                    <div className="p-3 bg-light border-bottom">
                      <div className="input-group mb-3">
                        <span className="input-group-text bg-white border-end-0">
                          <i className="bi bi-search text-muted"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0 ps-0"
                          placeholder="Search drivers..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={() => setSearchTerm("")}
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                        )}
                      </div>

                      {/* Filter Buttons */}
                      <div className="btn-group w-100" role="group">
                        <input
                          type="radio"
                          className="btn-check"
                          name="status"
                          id="all"
                          checked={filterStatus === "all"}
                          onChange={() => setFilterStatus("all")}
                        />
                        <label className="btn btn-outline-primary btn-sm" htmlFor="all">
                          <i className="bi bi-people-fill me-1"></i>
                          All ({users.length})
                        </label>

                        <input
                          type="radio"
                          className="btn-check"
                          name="status"
                          id="online"
                          checked={filterStatus === "online"}
                          onChange={() => setFilterStatus("online")}
                        />
                        <label className="btn btn-outline-success btn-sm" htmlFor="online">
                          <i className="bi bi-circle-fill me-1"></i>
                          Online ({onlineCount})
                        </label>

                        <input
                          type="radio"
                          className="btn-check"
                          name="status"
                          id="offline"
                          checked={filterStatus === "offline"}
                          onChange={() => setFilterStatus("offline")}
                        />
                        <label className="btn btn-outline-secondary btn-sm" htmlFor="offline">
                          <i className="bi bi-circle-fill me-1"></i>
                          Offline ({offlineCount})
                        </label>
                      </div>
                    </div>

                    {/* Show All Button */}
                    <div className="p-3 border-bottom">
                      <button
                        onClick={showAllUsersOnMap}
                        className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
                      >
                        <i className="bi bi-map"></i>
                        Show All on Map
                      </button>
                    </div>

                    {/* Driver List */}
                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                      {filteredUsers.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                          <i className="bi bi-search fs-1 d-block mb-2"></i>
                          <p className="mb-0">No drivers found</p>
                          {searchTerm && (
                            <button
                              className="btn btn-link btn-sm"
                              onClick={clearSearch}
                            >
                              Clear search
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="list-group list-group-flush">
                          {filteredUsers.map((user) => (
                            <button
                              key={user._id}
                              onClick={() => handleUserClick(user._id)}
                              className={`list-group-item list-group-item-action border-0 border-bottom px-3 py-3 ${
                                user.online === 1
                                  ? "list-group-item-light"
                                  : ""
                              }`}
                              style={{
                                transition: "all 0.2s ease",
                                borderLeft: user.online === 1 
                                  ? "3px solid #198754" 
                                  : "3px solid #6c757d"
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center gap-2">
                                  <UserAvatar user={user} />
                                  <div>
                                    <div className="fw-semibold text-dark">
                                      {user.username}
                                    </div>
                                    <small className="text-muted">
                                      {user.phone || "No phone"} · {user.online === 1 ? "Active now" : "Offline"}
                                    </small>
                                  </div>
                                </div>
                                <i className="bi bi-chevron-right text-muted"></i>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Map */}
              <div className="col-md-8">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body p-0">
                    <div
                      ref={mapRef}
                      style={{
                        height: "600px",
                        width: "100%",
                        borderRadius: "0.375rem",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default LiveUser;
